/**
 * usePresenterChannel — thin wrapper around BroadcastChannel for the
 * Worship Presenter. All output windows (audience/stage/confidence) and
 * the director console share one channel keyed by service id.
 *
 * Messages
 *   { kind: 'state',  payload: { slideIdx, blackout, freeze, theme } }
 *   { kind: 'hello',  role: 'output' | 'director' }
 *   { kind: 'sync?' }  // outputs ask for the latest state on mount
 *   { kind: 'bye' }
 *
 * The director is the source of truth; outputs echo back a `sync?` on
 * boot so late-openers immediately catch up.
 */
import { useEffect, useMemo, useRef } from 'react';

export function usePresenterChannel(serviceId, { role, onMessage } = {}) {
  const chanRef = useRef(null);
  const cbRef = useRef(onMessage);

  useEffect(() => { cbRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    if (!serviceId || typeof BroadcastChannel === 'undefined') return undefined;
    const name = `worship-present-${serviceId}`;
    const ch = new BroadcastChannel(name);
    chanRef.current = ch;
    ch.onmessage = (ev) => {
      if (cbRef.current) cbRef.current(ev.data);
    };
    // Say hello so peers know we're here
    try { ch.postMessage({ kind: 'hello', role: role || 'peer' }); } catch (e) { /* noop */ }
    return () => {
      try { ch.postMessage({ kind: 'bye', role }); } catch (e) { /* noop */ }
      ch.close();
      chanRef.current = null;
    };
  }, [serviceId, role]);

  return useMemo(() => ({
    post: (msg) => { try { chanRef.current?.postMessage(msg); } catch (e) { /* noop */ } },
  }), []);
}
